import { jsx, jsxs } from "react/jsx-runtime";
import { shade } from "polished";
import * as React from "react";
import { forwardRef } from "react";
import styled from "styled-components";
import { colors, semanticColors, typographyCss, cbSemanticCssVars } from "@creditornot/cb-ingredients";
import { createCssVars, createThemedCssVarDefinitions, themedConfigToThemedAttrs } from "@creditornot/cb-utils";
const SvgCross = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M2.44 19.44a1.5 1.5 0 002.121 2.121l7.262-7.26a.25.25 0 01.354 0l7.262 7.262a1.5 1.5 0 102.122-2.12l-7.26-7.266a.25.25 0 010-.354l7.262-7.262a1.5 1.5 0 00-2.124-2.12L12.177 9.7a.25.25 0 01-.354 0L4.561 2.44A1.5 1.5 0 002.44 4.56L9.7 11.823a.25.25 0 010 .354L2.44 19.44z" }) }));
const colorCssVars = createCssVars([
  "bg",
  "bgHover",
  "bgActive",
  "iconFill",
  "iconFillHover",
  "iconFillDisabled",
  "focusOutline"
]);
const BG = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  bottom: -4px;
  left: -4px;
  overflow: hidden;
  border-radius: 50%;

  transition: transform 96ms ease-in;
  z-index: 1;
`;
const IconContainer$1 = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: transparent;
  overflow: hidden;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Root$1 = styled.button`
  ${({ $colorConfig }) => createThemedCssVarDefinitions($colorConfig)}

  padding: 0;
  border: none;
  background: none;

  position: relative;
  flex-shrink: 0;
  z-index: 0;

  width: ${({ $iconSize }) => $iconSize}px;
  height: ${({ $iconSize }) => $iconSize}px;
  border-radius: 50%;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:not(:disabled) svg {
    fill: ${colorCssVars.iconFill};
  }

  ${BG} {
    background-color: ${colorCssVars.bg};
    transform: scale(0);
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled ${BG} {
    background-color: transparent;
  }

  &:hover:not(:disabled) {
    ${BG} {
      background-color: ${colorCssVars.bgHover};
      transform: scale(1);
    }

    svg {
      fill: ${colorCssVars.iconFillHover};
    }
  }

  &:active:not(:disabled) ${BG} {
    background-color: ${colorCssVars.bgActive};
  }
  &:disabled svg {
    fill: ${colorCssVars.iconFillDisabled};
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
  :focus-visible {
    outline: none;

    ${BG} {
      box-shadow: 0 0 0 2px ${colorCssVars.focusOutline};
      transform: scale(1);
    }
  }

  svg {
    transition: fill 96ms ease-in;
    width: ${({ $iconSize }) => $iconSize}px;
    height: ${({ $iconSize }) => $iconSize}px;
  }
`;
const TagRemoveButton = ({ children: icon, colorConfig, iconSize = 10, ...rest }) => /* @__PURE__ */ jsxs(Root$1, { $colorConfig: colorConfig, $iconSize: iconSize, ...rest, type: "button", children: [
  /* @__PURE__ */ jsx(BG, {}),
  /* @__PURE__ */ jsx(IconContainer$1, { children: icon })
] });
const removeButtonVariantConfigs = {
  blue: {
    bg: colors.wolt8,
    bgHover: colors.wolt8,
    bgActive: colors.wolt16,
    iconFill: colors.wolt100,
    iconFillHover: colors.wolt100,
    iconFillDisabled: colors.wolt40
  },
  blueForHighContrast: {
    bg: colors.darkSky8,
    bgHover: colors.darkSky8,
    bgActive: colors.darkSky16,
    iconFill: colors.darkSky100,
    iconFillHover: colors.darkSky100,
    iconFillDisabled: colors.darkSky40
  },
  darkBlue: {
    bg: colors.nightSky8,
    bgHover: colors.nightSky8,
    bgActive: colors.nightSky16,
    iconFill: colors.nightSky100,
    iconFillHover: colors.nightSky100,
    iconFillDisabled: colors.nightSky40
  },
  black: {
    bg: colors.pepper8,
    bgHover: colors.pepper8,
    bgActive: colors.pepper16,
    iconFill: colors.pepper100,
    iconFillHover: colors.pepper100,
    iconFillDisabled: colors.pepper40
  },
  green: {
    bg: colors.lime8,
    bgHover: colors.lime8,
    bgActive: colors.lime16,
    iconFill: colors.lime100,
    iconFillHover: colors.lime100,
    iconFillDisabled: colors.lime40
  },
  yellow: {
    bg: colors.banana8,
    bgHover: colors.banana8,
    bgActive: colors.banana16,
    iconFill: colors.banana100,
    iconFillHover: colors.banana100,
    iconFillDisabled: colors.banana40
  },
  orange: {
    bg: colors.orange8,
    bgHover: colors.orange8,
    bgActive: colors.orange16,
    iconFill: colors.orange100,
    iconFillHover: colors.orange100,
    iconFillDisabled: colors.orange40
  },
  red: {
    bg: colors.strawberry8,
    bgHover: colors.strawberry8,
    bgActive: colors.strawberry16,
    iconFill: colors.strawberry100,
    iconFillHover: colors.strawberry100,
    iconFillDisabled: colors.strawberry40
  },
  white: {
    bg: colors.salt8,
    bgHover: colors.salt8,
    bgActive: colors.salt16,
    iconFill: colors.salt100,
    iconFillHover: colors.salt100,
    iconFillDisabled: colors.salt40
  }
};
const variantConfigs = {
  blue: {
    light: {
      borderColor: "transparent",
      bg: colors.wolt100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: colors.darkSky100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.lightHighContrast.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.wolt100,
      color: colors.space100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  darkBlue: {
    light: {
      borderColor: "transparent",
      bg: colors.nightSky100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.nightSky100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  black: {
    light: {
      borderColor: "transparent",
      bg: colors.pepper100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.salt100,
      color: colors.space100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  green: {
    light: {
      borderColor: "transparent",
      bg: colors.lime100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: shade(0.31)(colors.lime100),
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.lime100,
      color: colors.space100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  yellow: {
    light: {
      borderColor: "transparent",
      bg: colors.banana100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: shade(0.37)(colors.banana100),
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.banana100,
      color: colors.space100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    }
  },
  orange: {
    light: {
      borderColor: "transparent",
      bg: colors.orange100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: shade(0.2)(colors.orange100),
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.orange100,
      color: colors.space100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    }
  },
  red: {
    light: {
      borderColor: "transparent",
      bg: colors.strawberry100,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: shade(0.12)(colors.strawberry100),
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.strawberry100,
      color: colors.space100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    }
  },
  lightBlue: {
    light: {
      borderColor: "transparent",
      bg: colors.wolt8,
      color: colors.wolt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.blue,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: colors.darkSky8,
      color: shade(0.1)(colors.darkSky100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.blueForHighContrast,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.wolt12,
      color: colors.wolt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.blue,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  lightDarkBlue: {
    light: {
      borderColor: "transparent",
      bg: colors.nightSky8,
      color: colors.nightSky100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.darkBlue,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.nightSky64,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  lightBlack: {
    light: {
      borderColor: "transparent",
      bg: colors.pepper8,
      color: colors.pepper100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.salt16,
      color: colors.salt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  lightGreen: {
    light: {
      borderColor: "transparent",
      bg: colors.lime8,
      color: colors.lime100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.green,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: colors.lime8,
      color: shade(0.5)(colors.lime100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.green,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.lime12,
      color: colors.lime100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.green,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  lightYellow: {
    light: {
      borderColor: "transparent",
      bg: colors.banana8,
      color: colors.banana100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.yellow,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: colors.banana8,
      color: shade(0.38)(colors.banana100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.yellow,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.banana12,
      color: colors.banana100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.yellow,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  lightOrange: {
    light: {
      borderColor: "transparent",
      bg: colors.orange8,
      color: colors.orange100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.orange,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: colors.orange8,
      color: shade(0.3)(colors.orange100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.orange,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.orange12,
      color: colors.orange100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.orange,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  lightRed: {
    light: {
      borderColor: "transparent",
      bg: colors.strawberry8,
      color: colors.strawberry100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.red,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: "transparent",
      bg: colors.strawberry8,
      color: shade(0.17)(colors.strawberry100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.red,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: colors.strawberry12,
      color: colors.strawberry100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.red,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  white: {
    light: {
      borderColor: "transparent",
      bg: semanticColors.light.surfaceMain,
      color: semanticColors.light.textPrimary,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: "transparent",
      bg: semanticColors.dark.surfaceMain,
      color: semanticColors.dark.textPrimary,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  outlineBlue: {
    light: {
      borderColor: colors.wolt100,
      bg: "transparent",
      color: colors.wolt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.blue,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: colors.darkSky100,
      bg: "transparent",
      color: colors.darkSky100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.blueForHighContrast,
        focusOutline: semanticColors.lightHighContrast.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: colors.wolt100,
      bg: "transparent",
      color: colors.wolt100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.blue,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  outlineBlack: {
    light: {
      borderColor: colors.pepper100,
      bg: "transparent",
      color: semanticColors.light.textPrimary,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.black,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: colors.salt100,
      bg: "transparent",
      color: semanticColors.dark.textPrimary,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.white,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  outlineGreen: {
    light: {
      borderColor: colors.lime100,
      bg: "transparent",
      color: colors.lime100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.green,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: shade(0.4)(colors.lime100),
      bg: "transparent",
      color: shade(0.4)(colors.lime100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.green,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: colors.lime100,
      bg: "transparent",
      color: colors.lime100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.green,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  outlineYellow: {
    light: {
      borderColor: colors.banana100,
      bg: "transparent",
      color: colors.banana100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.yellow,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: shade(0.38)(colors.banana100),
      bg: "transparent",
      color: shade(0.38)(colors.banana100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.yellow,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: colors.banana100,
      bg: "transparent",
      color: colors.banana100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.yellow,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  outlineOrange: {
    light: {
      borderColor: colors.orange100,
      bg: "transparent",
      color: colors.orange100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.orange,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: shade(0.2)(colors.orange100),
      bg: "transparent",
      color: shade(0.2)(colors.orange100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.orange,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: colors.orange100,
      bg: "transparent",
      color: colors.orange100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.orange,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  },
  outlineRed: {
    light: {
      borderColor: colors.strawberry100,
      bg: "transparent",
      color: colors.strawberry100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.red,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    lightHighContrast: {
      borderColor: shade(0.15)(colors.strawberry100),
      bg: "transparent",
      color: shade(0.15)(colors.strawberry100),
      removeButtonConfig: {
        ...removeButtonVariantConfigs.red,
        focusOutline: semanticColors.light.a11yFocusIndicator
      }
    },
    dark: {
      borderColor: colors.strawberry100,
      bg: "transparent",
      color: colors.strawberry100,
      removeButtonConfig: {
        ...removeButtonVariantConfigs.red,
        focusOutline: semanticColors.dark.a11yFocusIndicator
      }
    }
  }
};
const variantCssVars = createCssVars([
  "borderColor",
  "bg",
  "color"
]);
const sizeConfigs = {
  tiny: {
    height: 20,
    iconSize: 12,
    removeButtonSize: 8,
    typography: {
      lowercase: typographyCss.Tiny(),
      uppercase: typographyCss.Overline4()
    }
  },
  small: {
    height: 24,
    iconSize: 12,
    removeButtonSize: 10,
    typography: {
      lowercase: typographyCss.Small(),
      uppercase: typographyCss.Overline3()
    }
  }
};
const IconContainer = styled.div`
  padding-inline-end: ${({ $position }) => $position === "left" ? 4 : 0}px;
  padding-inline-start: ${({ $position }) => $position === "right" ? 4 : 0}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Root = styled.div`
  ${({ $variant }) => {
  var _a, _b, _c;
  return createThemedCssVarDefinitions({
    bg: {
      light: variantConfigs[$variant].light.bg,
      lightHighContrast: (_a = variantConfigs[$variant].lightHighContrast) == null ? void 0 : _a.bg,
      dark: variantConfigs[$variant].dark.bg
    },
    borderColor: {
      light: variantConfigs[$variant].light.borderColor,
      lightHighContrast: (_b = variantConfigs[$variant].lightHighContrast) == null ? void 0 : _b.borderColor,
      dark: variantConfigs[$variant].dark.borderColor
    },
    color: {
      light: variantConfigs[$variant].light.color,
      lightHighContrast: (_c = variantConfigs[$variant].lightHighContrast) == null ? void 0 : _c.color,
      dark: variantConfigs[$variant].dark.color
    }
  });
}}

  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  flex-shrink: 0;

  height: ${({ $size }) => sizeConfigs[$size].height}px;
  padding: 2px 8px;
  background-color: ${variantCssVars.bg};
  color: ${variantCssVars.color};
  border: 1px solid ${variantCssVars.borderColor};

  ${IconContainer} svg {
    width: ${({ $size }) => sizeConfigs[$size].iconSize}px;
    fill: ${variantCssVars.color};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${cbSemanticCssVars.a11yFocusIndicator};
  }
`;
const Label = styled.div`
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ $size, $uppercase }) => $uppercase ? sizeConfigs[$size].typography.uppercase : sizeConfigs[$size].typography.lowercase}
  font-weight: 500;
  display: flex;
  align-items: center;
`;
const RemoveButtonContainer = styled.div`
  padding-inline-start: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Tag = React.forwardRef(
  ({
    children,
    icon,
    iconPosition = "left",
    onRemoveClick,
    removeButtonAriaLabel,
    removeButtonProps = {},
    removeDisabled,
    size = "small",
    uppercase = false,
    variant = "blue",
    ...rest
  }, ref) => /* @__PURE__ */ jsxs(Root, { $size: size, $variant: variant, "data-variant": variant, ref, ...rest, children: [
    !!icon && iconPosition === "left" && /* @__PURE__ */ jsx(IconContainer, { $position: iconPosition, children: icon }),
    /* @__PURE__ */ jsx(Label, { $size: size, $uppercase: uppercase, children }),
    !!icon && iconPosition === "right" && /* @__PURE__ */ jsx(IconContainer, { $position: iconPosition, children: icon }),
    !!(onRemoveClick || removeButtonProps.onClick) && /* @__PURE__ */ jsx(RemoveButtonContainer, { children: /* @__PURE__ */ jsx(
      TagRemoveButton,
      {
        ...removeButtonProps,
        "aria-label": removeButtonAriaLabel || removeButtonProps["aria-label"] || (typeof children === "string" ? `Remove ${children}` : "Remove"),
        colorConfig: themedConfigToThemedAttrs({
          light: variantConfigs[variant].light.removeButtonConfig,
          dark: variantConfigs[variant].dark.removeButtonConfig
        }),
        disabled: removeDisabled ?? removeButtonProps.disabled,
        iconSize: sizeConfigs[size].removeButtonSize,
        onClick: onRemoveClick || removeButtonProps.onClick,
        children: /* @__PURE__ */ jsx(SvgCross, {})
      }
    ) })
  ] })
);
Tag.displayName = "Tag";
export {
  Tag
};
//# sourceMappingURL=index.js.map
